import { AlertTriangle, Check, Loader2 } from 'lucide-react'

import type { OptimizationBlockState } from '../../../lib/optimization/types'
import { resolveBlockDurationMs } from '../../../lib/optimization/blockDuration'

interface OptimizationBlockBadgeProps {
  block?: OptimizationBlockState
  onFailureClick?: (block: OptimizationBlockState) => void
}

function formatMs(ms: number | undefined): string {
  if (ms == null) return ''
  if (ms < 1000) return `${ms}ms`
  return `${(ms / 1000).toFixed(1)}s`
}

export default function OptimizationBlockBadge({
  block,
  onFailureClick,
}: OptimizationBlockBadgeProps) {
  if (!block || block.status === 'pending') return null

  if (block.status === 'running') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-brand-primary-subtle px-1.5 py-0.5 text-[10px] font-semibold text-brand-primary border border-brand-primary/20"
        data-testid={`opt-badge-${block.block_id}`}
      >
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        <span>…</span>
      </span>
    )
  }

  if (block.status === 'skipped') {
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700 border border-slate-200"
        data-testid={`opt-badge-${block.block_id}`}
        title="Skipped — unchanged since last successful run"
      >
        <Check className="h-3 w-3" aria-hidden />
        <span>skip</span>
      </span>
    )
  }

  if (block.status === 'done') {
    const durationMs = resolveBlockDurationMs(block)
    return (
      <span
        className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 border border-emerald-200"
        data-testid={`opt-badge-${block.block_id}`}
        title={durationMs != null ? `Completed in ${formatMs(durationMs)}` : undefined}
      >
        <Check className="h-3 w-3" aria-hidden />
        <span>{formatMs(durationMs)}</span>
      </span>
    )
  }

  if (block.status === 'failed') {
    return (
      <button
        type="button"
        className="inline-flex items-center gap-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900 border border-amber-300 hover:bg-amber-100"
        data-testid={`opt-badge-fail-${block.block_id}`}
        title="View agent feedback"
        onClick={(e) => {
          e.stopPropagation()
          onFailureClick?.(block)
        }}
      >
        <AlertTriangle className="h-3.5 w-3.5 text-amber-600" aria-hidden />
        <span>!</span>
      </button>
    )
  }

  return null
}
